import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Lang, UserType } from '@/data/constants';

import { normalizeRole } from '@/lib/roles';



export interface Profile {
  id: string;
  email: string;
  user_type: UserType;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  /** OPTIONAL — collected only in Profile Settings, never at registration. */
  license_number?: string | null;
  license_state?: string | null;
  insurance_carrier?: string | null;
  insurance_policy?: string | null;
  /** Set by admins only, after a real verification has been completed. */
  license_verified?: boolean | null;
  insurance_verified?: boolean | null;
  background_verified?: boolean | null;
  terms_accepted_at?: string | null;
  terms_version?: string | null;
  years_in_business?: number | null;
  employees?: number | null;
  service_areas?: string[] | null;
  trades?: string[] | null;
  preferred_language: Lang;
  status: string;
  is_admin: boolean;
  credits: number;
  /**
   * One-off $29 credits (mkt_profiles.extra_credits). Optional because some
   * profile reads project a narrower column list; treat a missing value as 0.
   * Spent only after the plan allowance for the cycle is used up.
   */
  extra_credits?: number;

}


interface AuthCtx {
  user: { id: string; email: string } | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, data: Partial<Profile>) => Promise<{ error?: string }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (patch: Partial<Profile>) => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export const crmSync = async (payload: Record<string, unknown>) => {
  try {
    await supabase.functions.invoke('crm-sync', { body: payload });
  } catch (_e) {
    /* non blocking */
  }
};

/**
 * Adds / updates a CRM contact (with Preferred Language) through our own
 * server-side endpoint. No external vendor domain is referenced from the
 * browser bundle — required by the white-label policy.
 */
export const crmSubscribe = async (payload: {
  email: string;
  name?: string;
  phone?: string;
  sms_opt_in?: boolean;
  source: string;
  tags?: string[];
  language?: string;
}) => {
  try {
    await supabase.functions.invoke('crm-subscribe', { body: payload });
  } catch (_e) {
    /* non blocking */
  }
};


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (id: string, email: string) => {
    const { data } = await supabase.from('mkt_profiles').select('*').eq('id', id).maybeSingle();
    if (data) {
      // NOTE: admin rights are NEVER granted from the browser. The old
      // client-side "promote ADMIN_EMAIL to is_admin" write has been removed —
      // a BEFORE UPDATE trigger (mkt_profiles_guard) now freezes is_admin,
      // credits, status, user_type and the verification flags for every
      // non-admin caller, so changing them from the console or a crafted REST
      // call silently reverts. Admin rights are set server-side only
      // (admin-set-role edge function / direct DB migration).
      setProfile(data as Profile);
      return;
    }
    // No profile row yet (e.g. the insert was interrupted right after signup).
    // The account type is recovered from the auth metadata written at signup so
    // a homeowner can never be silently re-created as a contractor account.
    const { data: authData } = await supabase.auth.getUser();
    const meta = (authData?.user?.user_metadata || {}) as Record<string, unknown>;
    const fallback = {
      id,
      email,
      user_type: normalizeRole(meta.user_type as string | undefined),
      first_name: (meta.first_name as string) || null,
      last_name: (meta.last_name as string) || null,
      company_name: (meta.company_name as string) || null,
      phone: (meta.phone as string) || null,
      preferred_language: (meta.preferred_language as string) || 'en',
      status: 'approved',
    };
    const { data: created } = await supabase.from('mkt_profiles').insert(fallback).select('*').maybeSingle();
    if (created) setProfile(created as Profile);
  }, []);



  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      const s = data.session;
      if (s?.user && mounted) {
        setUser({ id: s.user.id, email: s.user.email || '' });
        await loadProfile(s.user.id, s.user.email || '');
      }
      if (mounted) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setUser({ id: session.user.id, email: session.user.email || '' });
        await loadProfile(session.user.id, session.user.email || '');
      } else {
        setUser(null);
        setProfile(null);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signUp: AuthCtx['signUp'] = async (email, password, data) => {
    // The account type is written into the auth user metadata FIRST so it can
    // never be lost or defaulted to the wrong role later.
    const role = normalizeRole(data.user_type as string | undefined);
    const { data: res, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          user_type: role,
          first_name: data.first_name || null,
          last_name: data.last_name || null,
          company_name: data.company_name || null,
          phone: data.phone || null,
          preferred_language: data.preferred_language || 'en',
        },
      },
    });
    if (error) return { error: error.message };
    const id = res.user?.id;
    if (!id) return { error: 'Signup failed' };

    // PRIVILEGED FIELDS ARE NEVER SENT FROM THE BROWSER.
    // is_admin, credits, extra_credits, status and the verification flags are
    // deliberately absent from this payload. A BEFORE INSERT trigger
    // (mkt_profiles_insert_guard) recomputes them server-side from the trusted
    // auth identity + the mkt_admin_emails allowlist, so even a crafted REST
    // call that sets is_admin:true is neutralised before the row is written.
    const row = {
      id,
      email,
      user_type: role,
      first_name: data.first_name || null,
      last_name: data.last_name || null,
      company_name: data.company_name || null,
      contact_name: data.contact_name || null,
      phone: data.phone || null,
      city: data.city || null,
      state: data.state || null,
      // NOTE: license_number is intentionally NOT collected at registration.
      // It is optional and can be added later in Profile Settings.
      years_in_business: data.years_in_business || null,
      employees: data.employees || null,
      service_areas: data.service_areas || [],
      trades: data.trades || [],
      preferred_language: data.preferred_language || 'en',
      terms_accepted_at: data.terms_accepted_at || new Date().toISOString(),
      terms_version: data.terms_version || null,
    };
    const { data: inserted, error: insErr } = await supabase
      .from('mkt_profiles')
      .insert(row)
      .select('*')
      .maybeSingle();
    let saved = inserted;
    if (insErr || !saved) {
      // Retry once as an upsert so the account can never end up without a
      // profile row (which is what previously caused the wrong-role fallback).
      const { data: upserted } = await supabase
        .from('mkt_profiles')
        .upsert(row, { onConflict: 'id' })
        .select('*')
        .maybeSingle();
      saved = upserted;
    }
    // Always trust the row the DATABASE returned (post-trigger), never the
    // payload we sent — that is what makes the admin flag server-authoritative.
    setProfile((saved || { ...row, status: 'approved', is_admin: false, credits: 0 }) as Profile);
    setUser({ id, email });



    // Immutable record that this user accepted the marketplace terms.
    await supabase.from('mkt_acknowledgements').insert({
      user_id: id,
      email,
      context: `registration-${row.user_type}`,
      terms_version: row.terms_version || 'unversioned',
      language: row.preferred_language,
      detail: 'Accepted Terms of Service, Privacy Policy and marketplace disclaimer at signup',
    });

    const displayName = row.company_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || email;
    await crmSubscribe({
      email,
      name: displayName,
      phone: row.phone || undefined,
      sms_opt_in: true,
      source: `register-${row.user_type}`,
      tags: [row.user_type, `lang:${row.preferred_language}`],
      language: row.preferred_language,
    });
    await crmSync({
      event: 'welcome',
      email,
      name: displayName,
      phone: row.phone,
      language: row.preferred_language,
      userType: row.user_type,
    });
    await supabase.from('mkt_activity').insert({ user_id: id, action: 'register', detail: row.user_type });
    return {};
  };


  const signIn: AuthCtx['signIn'] = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    if (data.user) {
      setUser({ id: data.user.id, email: data.user.email || '' });
      await loadProfile(data.user.id, data.user.email || '');
    }
    return {};
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    // Send the user to the dedicated reset page (localized), where the recovery
    // token in the URL hash is exchanged for a session before the new password
    // is saved. Falls back to /en/reset-password when no language prefix exists.
    const seg = window.location.pathname.split('/')[1];
    const lang = seg === 'es' ? 'es' : 'en';
    const target = lang === 'es' ? '/es/restablecer-contrasena' : '/en/reset-password';
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${target}`,
    });
  };


  const refreshProfile = async () => {
    if (user) await loadProfile(user.id, user.email);
  };

  const updateProfile = async (patch: Partial<Profile>) => {
    if (!user) return;
    await supabase.from('mkt_profiles').update(patch).eq('id', user.id);
    setProfile((p) => (p ? { ...p, ...patch } : p));
  };

  return (
    <Ctx.Provider value={{ user, profile, loading, signUp, signIn, signOut, resetPassword, refreshProfile, updateProfile }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = (): AuthCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
